// import { debounce } from "lodash"
import { useSyncedReducer } from "@pathwright/blocks-engine"
import PropTypes from "prop-types"
import { memo, useCallback, useEffect, useMemo } from "react"
import isEqual from "react-fast-compare"
import { DragSortContainer, DragSortElement, DragSortHandle } from "../DragSort"
import { types } from "./actions"
import MotionReorder from "./MotionReorder"
import { getInitialState, reducer } from "./state"

const ListItem = memo(
  ({
    index,
    item,
    isAtMin,
    isActive,
    isDisabled,
    onRemove,
    handleUpdateItem,
    onSetActiveIndex,
    renderItem,
    DragSortHandle
  }) => {
    const handleSetActiveIndex = useCallback(() => {
      return onSetActiveIndex(index)
    }, [index])

    const handleRemoveItem = useCallback(() => onRemove(index), [index])

    return (
      <DragSortElement index={index} disabled={isDisabled}>
        {() =>
          renderItem({
            onRemove: handleRemoveItem,
            onUpdate: handleUpdateItem(index),
            onSetActive: handleSetActiveIndex,
            item,
            isAtMin,
            isActive,
            index,
            DragHandle: DragSortHandle
          })
        }
      </DragSortElement>
    )
  },
  (prevProps, nextProps) => {
    if (process.env.NODE_ENV === "production")
      return isEqual(prevProps, nextProps)

    if (!isEqual(prevProps, nextProps)) {
      // console.log(
      //   `list item ${nextProps.index} changed: `,
      //   diff(prevProps, nextProps)
      // )
      if (isEqual(prevProps.item, nextProps.item)) {
        // console.log(`list item ${nextProps.index} updated with no data change.`)
      }
      return false
    }

    return true
  }
)

ListItem.displayName = "ListItem"

const defaultRenderItem = ({ item }) => (
  <ul>
    {Object.keys(item).map((k) => (
      <li>{`${k}: ${item[k]}`}</li>
    ))}
  </ul>
)

const List = memo(
  (props) => {
    const {
      className = "",
      direction = "vertical",
      dragMode = "legacy",
      listStyles,
      itemContainerStyles,
      inlineAdd = false,
      renderAdd,
      min = 1,
      disabledIndices = [],
      onSetActiveIndex,
      onChange,
      generateItem,
      renderItem = defaultRenderItem,
      items: propsItems = [],
      autoGenerateItem,
      checkIsAutoGenerated
    } = props

    const syncer = useCallback(
      (action, state, dispatch) => {
        const { type, payload } = action

        switch (type) {
          case types.ADD_ITEM:
          case types.REMOVE_ITEM:
          case types.UPDATE_ITEM:
          case types.UPDATE_ITEM_ORDER:
            // Remove auto-generated item
            const nextItems = state.items.filter((item) => {
              if (checkIsAutoGenerated && checkIsAutoGenerated(item)) {
                return false
              }
              return true
            })

            onChange({ items: nextItems, changeType: payload.changeType })
            if (onSetActiveIndex) onSetActiveIndex(state.activeIndex)
            break
          case types.SET_ACTIVE_INDEX:
            if (onSetActiveIndex) onSetActiveIndex(payload.index)
            break
        }
      },
      [onChange]
    )

    const [state, dispatch] = useSyncedReducer(
      reducer,
      syncer,
      props,
      getInitialState
    )

    const { items, itemOrderIds, activeIndex } = state

    useEffect(() => {
      dispatch({
        type: types.SYNC_ITEMS,
        payload: {
          items: propsItems
        }
      })
    }, [propsItems])

    const handleSetActiveIndex = useCallback(
      (index) => {
        dispatch({
          type: types.SET_ACTIVE_INDEX,
          payload: {
            index
          }
        })
      },
      [types.SET_ACTIVE_INDEX, dispatch]
    )

    const handleAddItem = useCallback(() => {
      dispatch({
        type: types.ADD_ITEM,
        payload: {
          changeType: "add"
        }
      })
    }, [types.ADD_ITEM, dispatch])

    const handleRemoveItem = useCallback(
      (index) => {
        dispatch({
          type: types.REMOVE_ITEM,
          payload: {
            index,
            changeType: "remove"
          }
        })
      },
      [types.REMOVE_ITEM, dispatch]
    )

    const handleUpdateItem = useCallback(
      (index) =>
        ({ attribute, value, updateFunction = null }) => {
          dispatch({
            type: types.UPDATE_ITEM,
            payload: {
              index,
              attribute,
              value,
              updateFunction,
              changeType: "update"
            }
          })
        },
      [types.UPDATE_ITEM, dispatch]
    )

    const handleSort = useCallback(
      ({ oldIndex, newIndex, items: nextItems }) => {
        dispatch({
          type: types.UPDATE_ITEM_ORDER,
          payload: {
            oldIndex,
            newIndex,
            items: nextItems,
            changeType: "sort"
          }
        })
      },
      [types.UPDATE_ITEM_ORDER, dispatch]
    )

    const handleRenderAdd = useCallback(() => {
      if (!renderAdd || !generateItem) return null

      return renderAdd({ onAdd: handleAddItem })
    }, [renderAdd, generateItem, handleAddItem])

    const adjustedMin = useMemo(
      () => (autoGenerateItem ? min + 1 : min),
      [autoGenerateItem, min]
    )
    const isAtMin = useMemo(
      () => items.length <= adjustedMin,
      [items, adjustedMin]
    )

    if (dragMode === "motion") {
      return (
        <div className={`ListWrapper ${className}`} style={listStyles}>
          <MotionReorder
            items={items}
            itemOrderIds={itemOrderIds}
            activeIndex={activeIndex}
            disabledIndices={disabledIndices}
            autoGenerateItem={autoGenerateItem}
            checkIsAutoGenerated={checkIsAutoGenerated}
            isAtMin={isAtMin}
            itemContainerStyles={itemContainerStyles}
            renderItem={renderItem}
            onRemove={handleRemoveItem}
            onUpdate={handleUpdateItem}
            onSetActiveIndex={handleSetActiveIndex}
            onSort={handleSort}
          >
            {inlineAdd && handleRenderAdd()}
          </MotionReorder>
          {!inlineAdd && handleRenderAdd()}
        </div>
      )
    }

    return (
      <div className={`ListWrapper ${className}`} style={listStyles}>
        <DragSortContainer
          direction={direction}
          lock={true}
          onSort={handleSort}
          items={items}
        >
          {() => (
            <div className="ListItemContainer" style={itemContainerStyles}>
              {items.map((item, i) => {
                const isDisabled =
                  disabledIndices.includes(i) ||
                  (autoGenerateItem && checkIsAutoGenerated(item))

                return (
                  <ListItem
                    key={`${itemOrderIds[i]}`}
                    index={i}
                    item={item}
                    isAtMin={isAtMin}
                    isActive={activeIndex === i}
                    isDisabled={isDisabled}
                    renderItem={renderItem}
                    onRemove={handleRemoveItem}
                    handleUpdateItem={handleUpdateItem}
                    onSetActiveIndex={handleSetActiveIndex}
                    DragSortHandle={DragSortHandle}
                  />
                )
              })}
              {inlineAdd && handleRenderAdd()}
            </div>
          )}
        </DragSortContainer>
        {!inlineAdd && handleRenderAdd()}
      </div>
    )
  },
  (prevProps, nextProps) => {
    if (!isEqual(prevProps, nextProps)) {
      if (process.env.NODE_ENV !== "production") {
        // console.log(`list changed: `, diff(prevProps, nextProps))
      }

      return false
    }

    return true
  }
)

List.propTypes = {
  items: PropTypes.array.isRequired,
  // An id ensures unique keys for each list item
  id: PropTypes.string.isRequired,
  disabledIndices: PropTypes.arrayOf(PropTypes.number),
  renderItem: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  direction: PropTypes.oneOf(["vertical", "horizontal", "grid"]),
  dragMode: PropTypes.oneOf(["legacy", "motion"]),
  min: PropTypes.number,
  autoGenerateItem: PropTypes.bool,
  checkIsAutoGenerated: PropTypes.func,
  renderAdd: PropTypes.func,
  generateItem: PropTypes.func,
  inlineAdd: PropTypes.bool,
  listStyles: PropTypes.object,
  itemContainerStyles: PropTypes.object,
  className: PropTypes.string,
  onSetActiveIndex: PropTypes.func
}

List.displayName = "List"

export default List
